import { useEffect, useRef } from 'react';
import { showTooltip, hideTooltip } from './Tooltip';
import { formatAxis, formatWithUnit, formatNumber } from '../../lib/fmt';

export type BarSeries = { key: string; color?: string; label: string };
export type BarDatum = { label: string } & Record<string, number | string>;

// Les couleurs par défaut sont résolues via CSS vars — chaque thème définit sa palette.
const DEFAULT_COLORS = [
  'var(--chart-c1)',
  'var(--chart-c2)',
  'var(--chart-c3)',
  'var(--chart-c4)',
  'var(--chart-c5)',
  'var(--chart-c6)',
  'var(--chart-c7)',
];

export default function BarChart({
  data,
  series,
  height = 260,
  onClickBar,
  activeLabel,
  stacked = false,
  unit,
  horizontal = false,
  showValues = true,
}: {
  data: BarDatum[];
  series: BarSeries[];
  height?: number;
  onClickBar?: (d: BarDatum) => void;
  activeLabel?: string | null;
  stacked?: boolean;
  unit?: string | null;
  horizontal?: boolean;
  /** Afficher la valeur au sommet de chaque barre (style Power BI). */
  showValues?: boolean;
}) {
  const safeSeries = Array.isArray(series) ? series : [];
  const safeData: BarDatum[] = (Array.isArray(data) ? data : [])
    .filter((d): d is BarDatum => d !== null && d !== undefined)
    .map((d) => {
      const anyD = d as any;
      const label =
        typeof anyD.label === 'string' || typeof anyD.label === 'number'
          ? String(anyD.label)
          : String(Object.values(anyD).find((v) => typeof v === 'string') ?? '');
      return { ...anyD, label } as BarDatum;
    });

  const seriesWithColor = safeSeries.map((s, i) => ({
    ...s,
    color: s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length],
  }));

  // Plage de valeurs
  let minV = 0;
  let maxV = 0;
  for (const d of safeData) {
    if (stacked) {
      const sum = safeSeries.reduce((s, ser) => s + (Number(d[ser.key]) || 0), 0);
      maxV = Math.max(maxV, sum);
      minV = Math.min(minV, sum);
    } else {
      for (const ser of safeSeries) {
        const v = Number(d[ser.key]) || 0;
        if (v > maxV) maxV = v;
        if (v < minV) minV = v;
      }
    }
  }
  if (minV === 0 && maxV === 0) maxV = 1;

  // Toutes les valeurs entières ? → ticks entiers
  const allInts = safeData.every((d) =>
    safeSeries.every((s) => {
      const v = Number(d[s.key]);
      return Number.isFinite(v) && Math.abs(v - Math.round(v)) < 1e-6;
    })
  );

  const labels = safeData.map((d) => d.label);
  const maxLabelLen = Math.max(0, ...labels.map((l) => String(l).length));
  const needsRotation = horizontal ? false : safeData.length > 6 || maxLabelLen > 10;
  const rotatedLabelSpace = needsRotation ? Math.min(90, Math.round(maxLabelLen * 4.4)) : 0;

  const width = 560;
  const padL = horizontal ? Math.min(180, Math.max(80, maxLabelLen * 6.4)) : 64;
  const padR = 16;
  const padT = 32; // place pour la légende en haut + data labels
  const padB = 24 + rotatedLabelSpace;
  const w = width - padL - padR;
  const h = height - padT - padB;

  const range = maxV - minV || 1;
  const zeroY = padT + (maxV / range) * h;
  const vToY = (v: number) => padT + ((maxV - v) / range) * h;
  const vToX = (v: number) => padL + ((v - minV) / range) * w;
  const zeroX = vToX(0);

  const gref = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!gref.current) return;
    const rects = gref.current.querySelectorAll<SVGRectElement>('rect[data-bar]');
    rects.forEach((r, i) => {
      const finalH = Number(r.dataset.h ?? 0);
      const finalY = Number(r.dataset.y ?? 0);
      const finalW = Number(r.dataset.w ?? 0);
      const finalX = Number(r.dataset.x ?? 0);
      r.style.transition = 'none';
      if (horizontal) {
        r.setAttribute('width', '0');
        r.setAttribute('x', String(Number(r.dataset.origin ?? finalX)));
      } else {
        r.setAttribute('height', '0');
        r.setAttribute('y', String(Number(r.dataset.origin ?? finalY + finalH)));
      }
      requestAnimationFrame(() => {
        r.style.transition = `height 500ms ${i * 25}ms ease, y 500ms ${i * 25}ms ease, width 500ms ${i * 25}ms ease, x 500ms ${i * 25}ms ease, fill 120ms`;
        r.setAttribute('height', String(finalH));
        r.setAttribute('y', String(finalY));
        r.setAttribute('width', String(finalW));
        r.setAttribute('x', String(finalX));
      });
    });
  }, [safeData.map((d) => d.label).join('|'), seriesWithColor.map((s) => s.key).join('|'), horizontal]);

  const tickValues = buildTicks(minV, maxV, 5, allInts);

  const catSize = horizontal ? (safeData.length ? h / safeData.length : 0) : (safeData.length ? w / safeData.length : 0);
  const catPad = 0.15;

  // Data label au sommet d'une barre : JUSTE LE NOMBRE (l'unité vit dans l'axe/titre).
  // Exception : axe vide (pas d'unité affichée en ticks) → on colle l'unité quand même
  // pour garder la lecture non ambiguë (cas KPI isolé).
  const formatValueLabel = (v: number) => {
    if (v === 0) return '';
    return unit ? formatNumber(v) : formatWithUnit(v, unit);
  };

  // Anti-collision : estime la largeur en pixels d'un label à fontSize 10 (≈ 5.8 px/char)
  const estimateLabelWidth = (s: string): number => (s ? s.length * 5.8 : 0);
  // Espace disponible par bar group (grouped vertical) ou par bar (stacked / horizontal)
  const groupedBarW = seriesWithColor.length > 0 ? (catSize * (1 - catPad * 2)) / seriesWithColor.length : catSize;
  // Largeur maximale des data labels actuels (pour décider s'il faut les tourner ou les sauter)
  const widestLabel = Math.max(
    0,
    ...safeData.flatMap((d) => seriesWithColor.map((s) => estimateLabelWidth(formatValueLabel(Number(d[s.key]) || 0))))
  );
  // Règle : JAMAIS de rotation verticale d'un data-label sur une barre verticale.
  // Si le label le plus large dépasse la largeur disponible, on saute des labels
  // (1 sur 2, 1 sur 3…) pour garder la lecture horizontale.
  const rotateValueLabels = false;
  const labelOverflow = !horizontal && !stacked && widestLabel > groupedBarW - 4;
  const valueLabelStride = labelOverflow
    ? safeData.length > 24 ? 3 : safeData.length > 12 ? 2 : 1
    : 1;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible', fontFamily: 'inherit' }}>
      {/* Legend (en haut) */}
      {seriesWithColor.length > 0 && (seriesWithColor.length > 1 || seriesWithColor[0].label) && (
        <g transform={`translate(${padL} 4)`}>
          {seriesWithColor.map((s, i) => (
            <g key={s.key} transform={`translate(${i * 130} 0)`}>
              <rect width="10" height="10" style={{ fill: s.color }} rx="2" />
              <text x={14} y={9} fontSize="11" style={{ fill: 'var(--chart-text)' }}>
                {s.label}
              </text>
            </g>
          ))}
        </g>
      )}

      {/* Grid + ticks */}
      <g>
        {tickValues.map((v, i) => {
          if (horizontal) {
            const x = vToX(v);
            return (
              <g key={i}>
                <line x1={x} x2={x} y1={padT} y2={padT + h} style={{ stroke: 'var(--chart-grid)' }} strokeDasharray={v === 0 ? undefined : '2 3'} />
                <text x={x} y={padT + h + 14} textAnchor="middle" fontSize="10" style={{ fill: 'var(--chart-text-muted)' }}>
                  {formatAxis(v, unit)}
                </text>
              </g>
            );
          }
          const y = vToY(v);
          return (
            <g key={i}>
              <line x1={padL} x2={width - padR} y1={y} y2={y} style={{ stroke: 'var(--chart-grid)' }} strokeDasharray={v === 0 ? undefined : '2 3'} />
              <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" style={{ fill: 'var(--chart-text-muted)' }}>
                {formatAxis(v, unit)}
              </text>
            </g>
          );
        })}
      </g>

      <g ref={gref}>
        {safeData.map((d, i) => {
          const labelStr = String(d.label ?? '');
          const dimmed = activeLabel && activeLabel !== d.label;

          if (horizontal) {
            const rowY = padT + i * catSize + catSize * catPad;
            const rowH = catSize * (1 - catPad * 2);
            const barH = seriesWithColor.length ? rowH / seriesWithColor.length : rowH;
            return (
              <g
                key={labelStr || `bar-${i}`}
                style={{ cursor: onClickBar ? 'pointer' : 'default' }}
                onClick={() => onClickBar?.(d)}
                opacity={dimmed ? 0.4 : 1}
              >
                {seriesWithColor.map((s, si) => {
                  const v = Number(d[s.key]) || 0;
                  const x = v >= 0 ? zeroX : vToX(v);
                  const bw = Math.abs(vToX(v) - zeroX);
                  const y = rowY + si * barH;
                  return (
                    <g key={s.key}>
                      <rect
                        data-bar
                        data-h={barH - 2}
                        data-y={y}
                        data-w={bw}
                        data-x={x}
                        data-origin={zeroX}
                        x={x}
                        y={y}
                        width={bw}
                        height={Math.max(1, barH - 2)}
                        style={{ fill: s.color }}
                        rx={3}
                        onMouseMove={(e) =>
                          showTooltip({
                            x: e.clientX,
                            y: e.clientY,
                            title: labelStr,
                            rows: [{ label: s.label, value: formatWithUnit(v, unit) }],
                          })
                        }
                        onMouseLeave={hideTooltip}
                      />
                      {showValues && v !== 0 && (
                        <text
                          x={v >= 0 ? x + bw + 4 : x - 4}
                          y={y + (barH - 2) / 2 + 3}
                          textAnchor={v >= 0 ? 'start' : 'end'}
                          fontSize="10"
                          fontWeight={600}
                          style={{ fill: 'var(--chart-text)' }}
                          pointerEvents="none"
                        >
                          {formatValueLabel(v)}
                        </text>
                      )}
                    </g>
                  );
                })}
                <text
                  x={padL - 8}
                  y={rowY + rowH / 2 + 4}
                  textAnchor="end"
                  fontSize="11"
                  style={{ fill: 'var(--chart-text)' }}
                >
                  {labelStr}
                </text>
              </g>
            );
          }

          const colX = padL + i * catSize + catSize * catPad;
          const innerW = catSize * (1 - catPad * 2);

          if (stacked) {
            let runPos = 0;
            let runNeg = 0;
            const stackTop = safeSeries.reduce((s, ser) => s + Math.max(0, Number(d[ser.key]) || 0), 0);
            const topY = vToY(stackTop);
            return (
              <g
                key={labelStr || `bar-${i}`}
                style={{ cursor: onClickBar ? 'pointer' : 'default' }}
                onClick={() => onClickBar?.(d)}
                opacity={dimmed ? 0.4 : 1}
              >
                {seriesWithColor.map((s) => {
                  const v = Number(d[s.key]) || 0;
                  if (v === 0) return null;
                  let y: number;
                  let bh: number;
                  if (v >= 0) {
                    bh = (v / range) * h;
                    y = vToY(runPos + v);
                    runPos += v;
                  } else {
                    bh = (-v / range) * h;
                    y = vToY(runNeg);
                    runNeg += v;
                  }
                  return (
                    <rect
                      key={s.key}
                      data-bar
                      data-h={bh}
                      data-y={y}
                      data-w={innerW}
                      data-x={colX}
                      data-origin={zeroY}
                      x={colX}
                      y={y}
                      width={innerW}
                      height={bh}
                      style={{ fill: s.color }}
                      rx={3}
                      onMouseMove={(e) =>
                        showTooltip({
                          x: e.clientX,
                          y: e.clientY,
                          title: labelStr,
                          rows: seriesWithColor.map((ss) => ({
                            label: ss.label,
                            value: formatWithUnit(Number(d[ss.key] ?? 0), unit),
                          })),
                        })
                      }
                      onMouseLeave={hideTooltip}
                    />
                  );
                })}
                {showValues && stackTop !== 0 && (
                  <text
                    x={colX + innerW / 2}
                    y={topY - 4}
                    textAnchor="middle"
                    fontSize="10"
                    fontWeight={600}
                    style={{ fill: 'var(--chart-text)' }}
                    pointerEvents="none"
                  >
                    {formatValueLabel(stackTop)}
                  </text>
                )}
              </g>
            );
          }

          // Grouped vertical
          const sw = seriesWithColor.length > 0 ? innerW / seriesWithColor.length : innerW;
          return (
            <g
              key={labelStr || `bar-${i}`}
              style={{ cursor: onClickBar ? 'pointer' : 'default' }}
              onClick={() => onClickBar?.(d)}
              opacity={dimmed ? 0.4 : 1}
            >
              {seriesWithColor.map((s, si) => {
                const v = Number(d[s.key]) || 0;
                const bh = (Math.abs(v) / range) * h;
                const y = v >= 0 ? vToY(v) : zeroY;
                const bx = colX + si * sw;
                return (
                  <g key={s.key}>
                    <rect
                      data-bar
                      data-h={bh}
                      data-y={y}
                      data-w={Math.max(1, sw - 2)}
                      data-x={bx}
                      data-origin={zeroY}
                      x={bx}
                      y={y}
                      width={Math.max(1, sw - 2)}
                      height={bh}
                      style={{ fill: s.color }}
                      rx={3}
                      onMouseMove={(e) =>
                        showTooltip({
                          x: e.clientX,
                          y: e.clientY,
                          title: labelStr,
                          rows: [{ label: s.label, value: formatWithUnit(v, unit) }],
                        })
                      }
                      onMouseLeave={hideTooltip}
                    />
                    {showValues && v !== 0 && i % valueLabelStride === 0 && (() => {
                      const cx = bx + (sw - 2) / 2;
                      const topY = v >= 0 ? y - 4 : y + bh + 11;
                      const lbl = formatValueLabel(v);
                      if (rotateValueLabels) {
                        // Rotation -90° : texte vertical, au-dessus de la barre (lit bas→haut)
                        const ry = v >= 0 ? y - 6 : y + bh + 6;
                        return (
                          <text
                            x={cx}
                            y={ry}
                            textAnchor={v >= 0 ? 'start' : 'end'}
                            fontSize="10"
                            fontWeight={600}
                            style={{ fill: 'var(--chart-text)' }}
                            pointerEvents="none"
                            transform={`rotate(-90 ${cx} ${ry})`}
                          >
                            {lbl}
                          </text>
                        );
                      }
                      return (
                        <text
                          x={cx}
                          y={topY}
                          textAnchor="middle"
                          fontSize="10"
                          fontWeight={600}
                          style={{ fill: 'var(--chart-text)' }}
                          pointerEvents="none"
                        >
                          {lbl}
                        </text>
                      );
                    })()}
                  </g>
                );
              })}
            </g>
          );
        })}
      </g>

      {/* X axis labels (vertical mode) */}
      {!horizontal && (
        <g>
          {safeData.map((d, i) => {
            const labelStr = String(d.label ?? '');
            const cx = padL + i * catSize + catSize / 2;
            if (needsRotation) {
              return (
                <text
                  key={i}
                  x={cx}
                  y={padT + h + 10}
                  textAnchor="end"
                  fontSize="10.5"
                  style={{ fill: 'var(--chart-text)' }}
                  transform={`rotate(-45 ${cx} ${padT + h + 10})`}
                >
                  <title>{labelStr}</title>
                  {labelStr}
                </text>
              );
            }
            return (
              <text
                key={i}
                x={cx}
                y={padT + h + 14}
                textAnchor="middle"
                fontSize="11"
                style={{ fill: 'var(--chart-text)' }}
              >
                <title>{labelStr}</title>
                {labelStr}
              </text>
            );
          })}
        </g>
      )}

      {minV < 0 && !horizontal && (
        <line x1={padL} x2={width - padR} y1={zeroY} y2={zeroY} style={{ stroke: 'var(--chart-text)' }} strokeWidth={1} />
      )}
      {minV < 0 && horizontal && (
        <line x1={zeroX} x2={zeroX} y1={padT} y2={padT + h} style={{ stroke: 'var(--chart-text)' }} strokeWidth={1} />
      )}
    </svg>
  );
}

/** Ticks adaptatifs. Si toutes les valeurs sont entières et plage < 10, force step = 1. */
function buildTicks(min: number, max: number, approx: number, allInts: boolean): number[] {
  if (min === max) return [min];
  const range = max - min;
  // Entiers uniquement quand max petit
  if (allInts && range <= 10) {
    const out: number[] = [];
    const start = Math.floor(min);
    const end = Math.ceil(max);
    for (let v = start; v <= end; v++) out.push(v);
    return out;
  }
  const rawStep = range / approx;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const candidates = allInts ? [1, 2, 5, 10] : [1, 2, 2.5, 5, 10];
  let step = candidates.map((c) => c * mag).find((s) => range / s <= approx * 1.6) ?? mag;
  if (allInts) step = Math.max(1, Math.round(step));
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = start; v <= end + 1e-9; v += step) {
    out.push(Math.round(v * 1e6) / 1e6);
  }
  return out;
}
